/**
 * SignModeToggle — two radio cards for choosing proof storage mode.
 *
 * Standard (default): proof stored in OMEGA only.
 * Embedded: proof stored in OMEGA AND inside the file bytes.
 *
 * Persists choice per actionType to localStorage.
 * Hidden entirely when VITE_EMBEDDED_PROOF_UI_ENABLED=false.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';

export type SignMode = 'standard' | 'embedded';

interface SignModeToggleProps {
  actionType: string;
  value: SignMode;
  onChange: (mode: SignMode) => void;
}

export function SignModeToggle({ actionType, value, onChange }: SignModeToggleProps) {
  const { t } = useTranslation('spark');
  const enabled = import.meta.env.VITE_EMBEDDED_PROOF_UI_ENABLED !== 'false';
  if (!enabled) return null;

  const [remember, setRemember] = useState(false);

  const handleChange = (mode: SignMode) => {
    onChange(mode);
    if (remember) {
      setStoredSignMode(actionType, mode);
    }
  };

  const handleRemember = (checked: boolean) => {
    setRemember(checked);
    if (checked) {
      setStoredSignMode(actionType, value);
    }
  };

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          role="radio"
          aria-checked={value === 'standard'}
          onClick={() => handleChange('standard')}
          className={cn(
            'flex cursor-pointer flex-col gap-1 rounded-lg border p-3 text-start transition-colors',
            value === 'standard'
              ? 'border-primary bg-primary/5'
              : 'border-border bg-surface hover:border-border-strong',
          )}
        >
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border',
                value === 'standard'
                  ? 'border-primary bg-primary'
                  : 'border-border-strong bg-canvas',
              )}
            >
              {value === 'standard' && (
                <span className="h-1.5 w-1.5 rounded-full bg-white" />
              )}
            </span>
            <span className="text-xs font-semibold text-ink">
              {t('sign_mode.standard_title')}
            </span>
          </div>
          <p className="ps-5 text-[11px] leading-snug text-ink-muted">
            {t('sign_mode.standard_subtitle')}
          </p>
        </button>

        <button
          type="button"
          role="radio"
          aria-checked={value === 'embedded'}
          onClick={() => handleChange('embedded')}
          className={cn(
            'flex cursor-pointer flex-col gap-1 rounded-lg border p-3 text-start transition-colors',
            value === 'embedded'
              ? 'border-primary bg-primary/5'
              : 'border-border bg-surface hover:border-border-strong',
          )}
        >
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border',
                value === 'embedded'
                  ? 'border-primary bg-primary'
                  : 'border-border-strong bg-canvas',
              )}
            >
              {value === 'embedded' && (
                <span className="h-1.5 w-1.5 rounded-full bg-white" />
              )}
            </span>
            <span className="text-xs font-semibold text-ink">
              {t('sign_mode.embedded_title')}
            </span>
          </div>
          <p className="ps-5 text-[11px] leading-snug text-ink-muted">
            {t('sign_mode.embedded_subtitle')}
          </p>
        </button>
      </div>

      <label className="flex cursor-pointer items-center gap-2 text-[11px] text-ink-muted">
        <input
          type="checkbox"
          checked={remember}
          onChange={(e) => handleRemember(e.target.checked)}
          className="h-3.5 w-3.5 rounded border-border accent-primary"
        />
        {t('sign_mode.remember')}
      </label>
    </div>
  );
}

export function getStoredSignMode(actionType: string): SignMode {
  try {
    const v = localStorage.getItem(`gi.signMode.${actionType}`);
    if (v === 'embedded') return 'embedded';
  } catch { /* ignore */ }
  return 'standard';
}

export function setStoredSignMode(actionType: string, mode: SignMode): void {
  try {
    localStorage.setItem(`gi.signMode.${actionType}`, mode);
  } catch { /* ignore */ }
}
